import { authFetch } from '../../csrf';
import { API_BASE_URL } from '../../config';

const BASE_URL = `${API_BASE_URL}/crm/customer-payments`;

/**
 * Download 3-column Excel Template (.xlsx)
 */
export const downloadTemplate = async () => {
  const response = await authFetch(`${BASE_URL}/template`, {
    method: 'GET',
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error('Failed to download payment upload template');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'Customer_Payment_Upload_Template.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Validate Uploaded Excel File (Read-Only)
 */
export const validateUpload = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await authFetch(`${BASE_URL}/validate`, {
    method: 'POST',
    credentials: 'include',
    body: formData
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Validation failed');
  }

  return data.data;
};

/**
 * Process Payments Execution for a Batch ID
 */
export const processPayments = async (batchId) => {
  const response = await authFetch(`${BASE_URL}/process`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ batchId })
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Processing failed');
  }

  return data.data;
};

/**
 * Poll Progress for a Batch ID
 */
export const getProgress = async (batchId) => {
  const response = await authFetch(`${BASE_URL}/progress/${batchId}`, {
    method: 'GET',
    credentials: 'include'
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Failed to poll progress');
  }

  return data.data;
};

/**
 * Download Invoice Settlement Report (.xlsx)
 */
export const downloadSettlementReport = async (batchId) => {
  const response = await authFetch(`${BASE_URL}/report/${batchId}`, {
    method: 'GET',
    credentials: 'include'
  });

  if (!response.ok) {
    let errorMsg = 'Failed to download settlement report';
    try {
      const errData = await response.json();
      if (errData.message) errorMsg = errData.message;
    } catch (_) {}
    throw new Error(errorMsg);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');

  // Extract filename from response header or generate timestamp
  const disposition = response.headers.get('content-disposition');
  let filename = '';
  if (disposition && disposition.includes('filename=')) {
    filename = disposition.split('filename=')[1].replace(/["']/g, '').trim();
  }

  if (!filename) {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const dateStr = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}`;
    const timeStr = `${pad(now.getHours())}-${pad(now.getMinutes())}`;
    filename = `Invoice_Settlement_Report_${batchId}_${dateStr}_${timeStr}.xlsx`;
  }

  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Fetch Batch Import Audit History
 */
export const getHistory = async (page = 1, limit = 10) => {
  const response = await authFetch(`${BASE_URL}/history?page=${page}&limit=${limit}`, {
    method: 'GET',
    credentials: 'include'
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Failed to fetch import history');
  }

  return data;
};
